import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Segmented, Table, Tag, Input, Button, Alert, Empty, Tooltip, Spin } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatDate } from '../../variables/constants';
import KpiCards from '../../components/KpiCards';
import i18n from '../../i18n';
import { avecLibelleTraduit } from '../../i18n/libelles';

/**
 * Historique des relances regroupé par organisme partenaire.
 *
 * <h3>Deux populations, jamais mélangées</h3>
 * Le bureau national suit d'un côté les **bureaux homologues** CEDEAO - ses pairs
 * à l'étranger - et de l'autre les **compagnies membres** togolaises qui lui sont
 * rattachées. On ne relance pas les deux pour les mêmes raisons ni avec les mêmes
 * leviers : un homologue se relance au titre de la Convention, un adhérent au titre
 * de son adhésion. Les fondre dans une liste unique effacerait cette distinction,
 * qui est précisément ce qu'on vient regarder.
 *
 * Alimenté par `GET /v1/delais/par-organisme?membres=`, qui renvoie le regroupement
 * déjà trié par criticité : cet écran sert à décider qui relancer aujourd'hui, pas
 * à consulter un annuaire.
 */

const NIVEAUX = avecLibelleTraduit({
  CRITIQUE: { color: '#dc2626' },
  URGENT: { color: '#ea580c' },
  ATTENTION: { color: '#ca8a04' },
}, 'relances:niveaux');

const MUTED = '#6b7280';

export default function RelancesParOrganismeTab() {
  const { t } = useTranslation('relances');
  const navigate = useNavigate();
  const [population, setPopulation] = useState('homologues');
  const [lignes, setLignes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [recherche, setRecherche] = useState('');

  const membres = population === 'membres';

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const r = await axiosInstance.get('/v1/delais/par-organisme', { params: { membres } });
      setLignes(r.data?.data || []);
    } catch (err) {
      setErreur(err?.response?.data?.message || i18n.t('relances:parOrganisme.erreurChargement'));
      setLignes([]);
    } finally {
      setChargement(false);
    }
  }, [membres]);

  useEffect(() => { charger(); }, [charger]);

  const filtrees = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (!q) return lignes;
    return lignes.filter((l) => [l.organismeRaisonSociale, l.organismeCode, l.organismeCodePays]
      .filter(Boolean).some((c) => String(c).toLowerCase().includes(q)));
  }, [lignes, recherche]);

  const totaux = useMemo(() => filtrees.reduce((acc, l) => ({
    partenaires: acc.partenaires + 1,
    dossiers: acc.dossiers + (l.nbDossiersEnAttente || 0),
    critiques: acc.critiques + (l.nbCritiques || 0),
    relances: acc.relances + (l.totalAlertesEnvoyees || 0),
  }), { partenaires: 0, dossiers: 0, critiques: 0, relances: 0 }), [filtrees]);

  const kpis = [
    {
      label: membres ? t('parOrganisme.kpis.compagnies') : t('parOrganisme.kpis.bureaux'),
      value: chargement ? '…' : totaux.partenaires,
      color: '#1a6b3c',
    },
    {
      label: t('parOrganisme.kpis.dossiers'),
      value: chargement ? '…' : totaux.dossiers,
      color: '#2563eb',
    },
    {
      label: t('parOrganisme.kpis.critiques'),
      value: chargement ? '…' : totaux.critiques,
      color: '#dc2626',
    },
    {
      label: t('parOrganisme.kpis.relances'),
      value: chargement ? '…' : totaux.relances,
      color: '#ca8a04',
    },
  ];

  const colonnes = [
    {
      title: membres ? t('parOrganisme.colonnes.compagnie') : t('parOrganisme.colonnes.bureau'),
      key: 'organisme',
      render: (_, r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 650, fontSize: 13.5 }}>{r.organismeRaisonSociale || '-'}</div>
          <div style={{ fontSize: 12, color: MUTED }}>
            {[r.organismeCode, r.organismeCodePays].filter(Boolean).join(' · ') || '-'}
          </div>
        </div>
      ),
    },
    {
      title: t('parOrganisme.colonnes.dossiers'),
      dataIndex: 'nbDossiersEnAttente',
      width: 150,
      align: 'right',
      sorter: (a, b) => a.nbDossiersEnAttente - b.nbDossiersEnAttente,
      render: (v) => <span style={{ fontWeight: 700 }}>{v}</span>,
    },
    {
      title: t('parOrganisme.colonnes.repartition'),
      key: 'niveaux',
      width: 260,
      render: (_, r) => {
        const cases = [
          ['CRITIQUE', r.nbCritiques],
          ['URGENT', r.nbUrgents],
          ['ATTENTION', r.nbAttention],
        ].filter(([, n]) => n > 0);
        if (!cases.length) return <span style={{ color: '#9ca3af' }}>{t('parOrganisme.aucuneAlerte')}</span>;
        return (
          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {cases.map(([cle, n]) => (
              <Tag
                key={cle}
                style={{
                  borderRadius: 12, marginInlineEnd: 0, fontWeight: 650,
                  color: NIVEAUX[cle].color, borderColor: `${NIVEAUX[cle].color}55`,
                  background: `${NIVEAUX[cle].color}0f`,
                }}
              >
                {n} {NIVEAUX[cle].label.toLowerCase()}
              </Tag>
            ))}
          </span>
        );
      },
    },
    {
      title: t('parOrganisme.colonnes.relances'),
      dataIndex: 'totalAlertesEnvoyees',
      width: 150,
      align: 'right',
      sorter: (a, b) => a.totalAlertesEnvoyees - b.totalAlertesEnvoyees,
      render: (v) => <span style={{ color: MUTED }}>{v || 0}</span>,
    },
  ];

  /* Le detail d'un partenaire : ses dossiers, ligne a ligne. Deplie a la demande -
     tout afficher d'emblee ferait des milliers de lignes pour une question qui
     commence toujours par « chez qui ca coince ». */
  const detail = (r) => (
    <Table
      size="small"
      rowKey={(n) => n.id}
      dataSource={r.notifications || []}
      pagination={false}
      columns={[
        {
          title: t('parOrganisme.detail.sinistre'),
          dataIndex: 'sinistreNumeroLocal',
          width: 190,
          render: (v, n) => (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, fontWeight: 650 }}
              onClick={() => navigate(`/sinistres/${n.sinistreTrackingId}`)}
            >
              {v || '-'}
            </Button>
          ),
        },
        { title: t('parOrganisme.detail.delai'), dataIndex: 'libelleDelai', ellipsis: true },
        {
          title: t('parOrganisme.detail.echeance'),
          dataIndex: 'dateEcheance',
          width: 140,
          render: (v) => <span style={{ fontSize: 12.5 }}>{v ? formatDate(v) : '-'}</span>,
        },
        {
          title: t('parOrganisme.detail.reste'),
          dataIndex: 'joursRestants',
          width: 110,
          align: 'right',
          render: (v) => (
            <span style={{ fontWeight: 650, color: v < 0 ? '#dc2626' : v <= 5 ? '#ea580c' : MUTED }}>
              {v < 0 ? t('parOrganisme.detail.retard', { n: Math.abs(v) }) : t('parOrganisme.detail.jours', { n: v })}
            </span>
          ),
        },
        {
          title: t('parOrganisme.detail.niveau'),
          dataIndex: 'niveauAlerte',
          width: 120,
          render: (v) => {
            const m = NIVEAUX[v];
            return m
              ? <Tag style={{ borderRadius: 12, color: m.color, borderColor: `${m.color}55`, background: `${m.color}0f` }}>{m.label}</Tag>
              : <span style={{ color: '#9ca3af' }}>-</span>;
          },
        },
        {
          title: t('parOrganisme.detail.relances'),
          dataIndex: 'nombreAlertes',
          width: 100,
          align: 'right',
          render: (v) => <span style={{ color: MUTED }}>{v ?? 0}</span>,
        },
      ]}
    />
  );

  return (
    <div style={{ padding: '12px 0' }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <Segmented
          value={population}
          onChange={setPopulation}
          options={[
            { label: t('parOrganisme.populations.homologues'), value: 'homologues' },
            { label: t('parOrganisme.populations.membres'), value: 'membres' },
          ]}
        />
        <Input
          prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
          placeholder={t('parOrganisme.recherche')}
          allowClear
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          style={{ width: 280 }}
        />
        <Tooltip title={t('parOrganisme.recharger')}>
          <Button icon={<ReloadOutlined />} onClick={charger} />
        </Tooltip>
      </div>

      <KpiCards items={kpis} columns={4} />

      {erreur && (
        <Alert
          type="error"
          showIcon
          message={erreur}
          action={<Button size="small" onClick={charger}>{t('parOrganisme.reessayer')}</Button>}
          style={{ marginBottom: 16 }}
        />
      )}

      {chargement ? (
        <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
      ) : (
        <Table
          className="cb-table"
          rowKey="organismeTrackingId"
          dataSource={filtrees}
          columns={colonnes}
          size="middle"
          expandable={{ expandedRowRender: detail, rowExpandable: (r) => (r.notifications || []).length > 0 }}
          pagination={{ pageSize: 15, showSizeChanger: false, showTotal: (n) => t('parOrganisme.total', { count: n }) }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={membres
                  ? t('parOrganisme.videMembres')
                  : t('parOrganisme.videHomologues')}
              />
            ),
          }}
        />
      )}
    </div>
  );
}
